import React, { ReactNode, useMemo, useState } from "react";
import { Empty, Skeleton, Table, Tooltip } from "antd";
import { CaretDownOutlined, CaretRightOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { DaySheet, DaySheetTask } from "../../../../api/SiteDaily";
import { useFontScale } from "../../../../theme/fontScale";
import { GenericStatusPill, StatusTone } from "../../shared/statusColors";
import { useWbsTreeName } from "../../shared/useWbsCodes";
import { fmtQty } from "../executionNorm";

// Material-Stores, "Pending material": the tasks still owed material, read
// DATE-WISE — each civil date still owed (oldest first), then activity, then
// the tasks whose material for that day is not yet booked. A task owed for several dates sits
// under each of them. Opening a task from a date lands its drawer on that date.
//
// What is entered against a civil date is booked on that date exactly (the
// server keeps the split beside the day's issue).

export interface PendingDateTaskState {
  dirty?: boolean;
  saving?: boolean;
}

type Row =
  | { kind: "day"; key: string; workDate: string; taskCount: number; lineCount: number }
  | { kind: "activity"; key: string; workDate: string; activityId: number; activityName: string; taskCount: number; lineCount: number }
  | { kind: "task"; key: string; workDate: string; task: DaySheetTask; activityId: number; activityName: string; producedQty: string; lineCount: number; bookedTodayCount: number };

const UNSAVED: StatusTone = { dot: "#fa8c16", text: "#d46b08", bg: "#fff7e6" };
const OWED: StatusTone = { dot: "#d46b08", text: "#ad4e00", bg: "#fff7e6" };
const BOOKED: StatusTone = { dot: "#389e0d", text: "#237804", bg: "#f6ffed" };
const PART: StatusTone = { dot: "#2557d6", text: "#1d39c4", bg: "#f0f5ff" };

const PendingByDateList: React.FC<{
  sheet: DaySheet | null;
  loading: boolean;
  activeTaskId: number | null;
  activeDate: string | null;
  onOpen: (task: DaySheetTask, workDate: string) => void;
  taskState: (task: DaySheetTask) => PendingDateTaskState | undefined;
  emptyDescription: ReactNode;
  // Which dates a task is owed on (material by default; machine hours on the
  // Mechanical sheet), and what one owed line is called.
  datesOf?: (task: DaySheetTask) => DaySheetTask["pendingMaterialDates"];
  lineNoun?: [string, string];
  owedTitle?: string;
}> = ({
  sheet,
  loading,
  activeTaskId,
  activeDate,
  onOpen,
  taskState,
  emptyDescription,
  datesOf = (task) => task.pendingMaterialDates,
  lineNoun = ["material line", "material lines"],
  owedTitle = "Materials owed",
}) => {
  const { scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  // The row names the ACTIVITY (task names repeat across activities), labelled
  // the same way the phase / activity tree labels it.
  const activityNodes = useMemo(
    () =>
      (sheet?.phases || []).flatMap((phase) =>
        phase.activities.map((activity) => ({ key: `a:${activity.activityId}`, name: activity.activityName }))
      ),
    [sheet]
  );
  const activityLabel = useWbsTreeName(activityNodes);

  const rows = useMemo<Row[]>(() => {
    const byDate = new Map<string, Array<Extract<Row, { kind: "task" }>>>();
    (sheet?.phases || []).forEach((phase) =>
      phase.activities.forEach((activity) =>
        activity.tasks.forEach((task) =>
          datesOf(task).forEach((day) => {
            const list = byDate.get(day.workDate) ?? [];
            list.push({
              kind: "task",
              key: `${day.workDate}-${task.taskId}`,
              workDate: day.workDate,
              task,
              activityId: activity.activityId,
              activityName: activity.activityName,
              producedQty: day.producedQty,
              lineCount: day.lineCount,
              bookedTodayCount: day.bookedTodayCount,
            });
            byDate.set(day.workDate, list);
          })
        )
      )
    );
    const out: Row[] = [];
    Array.from(byDate.keys())
      .sort()
      .forEach((workDate) => {
        const tasks = byDate.get(workDate)!;
        const key = `D-${workDate}`;
        out.push({
          kind: "day",
          key,
          workDate,
          taskCount: tasks.length,
          lineCount: tasks.reduce((sum, t) => sum + t.lineCount, 0),
        });
        if (collapsed.has(key)) return;
        // Activity groups in the order the sheet lists them.
        const byActivity = new Map<number, Array<Extract<Row, { kind: "task" }>>>();
        tasks.forEach((t) => {
          const list = byActivity.get(t.activityId) ?? [];
          list.push(t);
          byActivity.set(t.activityId, list);
        });
        byActivity.forEach((list, activityId) => {
          const activityKey = `${key}-A-${activityId}`;
          out.push({
            kind: "activity",
            key: activityKey,
            workDate,
            activityId,
            activityName: list[0].activityName,
            taskCount: list.length,
            lineCount: list.reduce((sum, t) => sum + t.lineCount, 0),
          });
          if (!collapsed.has(activityKey)) out.push(...list);
        });
      });
    return out;
  }, [sheet, collapsed, datesOf]);

  const toggle = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const daySpan = (row: Row, key: string) => {
    if (row.kind === "task") return {};
    if (key === "name") return {};
    if (key === "produced") return { colSpan: 3 };
    return { colSpan: 0 };
  };

  const columns = [
    {
      title: "Civil date / Activity / Task",
      key: "name",
      width: w(480),
      render: (_: any, row: Row) => {
        if (row.kind === "day") {
          return (
            <span style={{ color: "#022c57" }}>
              <button type="button" className="pms-exec-caret" aria-label="Toggle date">
                {collapsed.has(row.key) ? <CaretRightOutlined /> : <CaretDownOutlined />}
              </button>
              {dayjs(row.workDate).format("DD-MMM-YYYY")}
              <span className="pms-exec-group-count">
                {row.taskCount} task{row.taskCount === 1 ? "" : "s"}
              </span>
            </span>
          );
        }
        if (row.kind === "activity") {
          return (
            <span style={{ color: "#364a63", paddingLeft: 18 }}>
              <button type="button" className="pms-exec-caret" aria-label="Toggle activity">
                {collapsed.has(row.key) ? <CaretRightOutlined /> : <CaretDownOutlined />}
              </button>
              {activityLabel(`a:${row.activityId}`, row.activityName)}
              <span className="pms-exec-group-count">
                {row.taskCount} task{row.taskCount === 1 ? "" : "s"}
              </span>
            </span>
          );
        }
        return (
          <span style={{ paddingLeft: 36, display: "inline-flex", alignItems: "center", minWidth: 0 }}>
            <span className="pms-exec-caret" aria-hidden>
              <RightOutlined style={{ fontSize: 10 }} />
            </span>
            <span className="pms-exec-task-seq">{row.task.taskSequenceId}</span>
            <Tooltip title={row.task.taskName.length > 40 ? row.task.taskName : ""}>
              <span
                className="pms-exec-task-name"
                style={{ maxWidth: w(480) - 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "bottom" }}
              >
                {row.task.taskName}
              </span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: "Produced that day",
      key: "produced",
      width: w(170),
      align: "right" as const,
      onCell: (row: Row) => daySpan(row, "produced"),
      render: (_: any, row: Row) => {
        if (row.kind !== "task") {
          return (
            <span className="pms-exec-hint" style={{ display: "block", textAlign: "left" }}>
              {row.lineCount} {row.lineCount === 1 ? lineNoun[0] : lineNoun[1]} owed
            </span>
          );
        }
        return (
          <span className="tabular-nums" style={{ color: "#022c57", fontWeight: 600 }}>
            {fmtQty(row.producedQty)} <span style={{ fontWeight: 400, color: "#8094ae" }}>{row.task.workUomName}</span>
          </span>
        );
      },
    },
    {
      title: owedTitle,
      key: "owed",
      width: w(140),
      align: "right" as const,
      onCell: (row: Row) => daySpan(row, "owed"),
      render: (_: any, row: Row) =>
        row.kind === "task" ? (
          <span className="tabular-nums" style={{ color: row.lineCount ? "#022c57" : "#8094ae" }}>
            {row.lineCount || "—"}
          </span>
        ) : null,
    },
    {
      title: "Status",
      key: "status",
      width: w(140),
      onCell: (row: Row) => daySpan(row, "status"),
      render: (_: any, row: Row) => {
        if (row.kind !== "task") return null;
        const state = taskState(row.task);
        if (state?.saving) return <GenericStatusPill label="Saving…" tone={UNSAVED} />;
        if (state?.dirty) return <GenericStatusPill label="Unsaved" tone={UNSAVED} />;
        if (row.lineCount === 0) return <GenericStatusPill label="Booked today" tone={BOOKED} />;
        if (row.bookedTodayCount > 0) return <GenericStatusPill label="Part booked" tone={PART} />;
        return <GenericStatusPill label="Owed" tone={OWED} />;
      },
    },
  ];

  if (loading && !sheet) {
    return (
      <div className="pms-exec-skeleton-card">
        <Skeleton active title={false} paragraph={{ rows: 6 }} />
      </div>
    );
  }

  return (
    <Table
      size="small"
      rowKey="key"
      className="pms-exec-sheet"
      loading={loading}
      dataSource={rows}
      columns={columns as any}
      pagination={false}
      rowClassName={(row: Row) =>
        [
          row.kind === "day" ? "pms-exec-row-phase" : row.kind === "activity" ? "pms-exec-row-activity" : "pms-exec-row-task",
          "pms-exec-row-clickable",
          row.kind === "task" && row.task.taskId === activeTaskId && row.workDate === activeDate ? "pms-exec-row-open" : "",
        ]
          .filter(Boolean)
          .join(" ")
      }
      onRow={(row: Row) => ({
        onClick: () => (row.kind === "task" ? onOpen(row.task, row.workDate) : toggle(row.key)),
      })}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} /> }}
    />
  );
};

export default PendingByDateList;
